// Reading a ticket's claim state as the admin ticket endpoints return it
// (docs/features/auth-sessions-and-ticket-claims.md, "Organizer"): `claimCode`,
// `claimedAt`, `claimedVia`, and the holder account summary. A ticket is
// claimed when it has a `userId`; the two timestamps only record how.

/** How a ticket got onto its account, as organizers read it. */
export const CLAIMED_VIA_LABELS = {
  email: "Signed in with the ticket email",
  link: "Ticket link",
  code: "Ticket code",
  registration: "Self-registration",
  organizer: "Organizer",
  migration: "Before tracking",
};

/** Whether the API sent claim fields at all (older APIs do not). */
export const hasClaimFields = (ticket) =>
  ticket != null && ("claimCode" in ticket || "claimedVia" in ticket);

export const isTicketClaimed = (ticket) => ticket?.userId != null;

/**
 * The account holding the ticket, or `null`: the `holderAccount` summary
 * (`{ id, firstName, lastName, email, isAnonymous }`) that GET /tickets and
 * GET /tickets/:id return to event admins.
 */
export function getClaimAccount(ticket) {
  if (!isTicketClaimed(ticket)) return null;

  const account = ticket.holderAccount ?? null;
  const name = [account?.firstName, account?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();

  return {
    id: account?.id ?? ticket.userId,
    name,
    email: account?.email ?? null,
    isAnonymous: Boolean(account?.isAnonymous),
    // No sign-in and no live session: out of the app for good.
    signedOut: Boolean(account?.signedOut),
  };
}

/** "ABCD2345" -> "ABCD 2345", easier to read out over the phone. */
export const formatClaimCode = (code) =>
  code && code.length > 4 ? `${code.slice(0, 4)} ${code.slice(4)}` : code;
